/**
 * Quotes React Query hooks
 *
 * This module contains React Query hooks for quotes operations.
 */

import { useMutation, useQuery, useQueryClient, type UseMutationOptions, type UseQueryOptions } from '@tanstack/react-query';
import * as quotesApi from '@/services/api/quotes';

/**
 * Query keys factory for quotes
 */
export const quoteKeys = {
  all: ['quotes'] as const,
  lists: () => [...quoteKeys.all, 'list'] as const,
  list: (params?: quotesApi.QuotesListParams) => [...quoteKeys.lists(), params] as const,
  details: () => [...quoteKeys.all, 'detail'] as const,
  detail: (id: string) => [...quoteKeys.details(), id] as const,
  calculations: () => [...quoteKeys.all, 'calculation'] as const,
  pricing: (params: quotesApi.CalculatePricingRequest) => [...quoteKeys.calculations(), params] as const,
  activities: (organizationId: string, limit?: number) => [...quoteKeys.all, 'activities', organizationId, limit] as const,
  summary: (organizationId: string) => [...quoteKeys.all, 'summary', organizationId] as const,
} as const;

// ==================== Client Hooks ====================

/**
 * Hook to fetch quotes list for an organization
 */
export function useQuotes(organizationId: string, params?: Omit<quotesApi.QuotesListParams, 'organizationId'>) {
  return useQuery({
    queryKey: quoteKeys.list({ ...params, organizationId }),
    queryFn: () => quotesApi.listQuotes(organizationId, params),
    enabled: !!organizationId,
  });
}

/**
 * Hook to fetch a single quote by ID
 */
type UseQuoteOptions = Omit<
  UseQueryOptions<quotesApi.QuoteWithDetails, Error, quotesApi.QuoteWithDetails, ReturnType<typeof quoteKeys.detail>>,
  'queryKey' | 'queryFn' | 'enabled'
>;

export function useQuote(id: string, enabled = true, options?: UseQuoteOptions) {
  return useQuery({
    queryKey: quoteKeys.detail(id),
    queryFn: () => quotesApi.getQuote(id),
    enabled: enabled && !!id,
    ...options,
  });
}

/**
 * Hook to create a new quote
 */
type UseCreateQuoteOptions = Omit<
  UseMutationOptions<
    { quote: quotesApi.Quote; pricing: quotesApi.PricingCalculation },
    Error,
    quotesApi.CreateQuoteRequest
  >,
  'mutationFn'
>;

export function useCreateQuote(options?: UseCreateQuoteOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: quotesApi.CreateQuoteRequest) => quotesApi.createQuote(data),
    onSuccess: (data) => {
      // Invalidate and refetch quotes list
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
      // Invalidate the detail query so it fetches fresh data with correct QuoteWithDetails structure
      // Note: We don't set cache here because createQuote returns { quote, pricing }
      // but useQuote expects QuoteWithDetails structure
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(data.quote.id) });
    },
    ...options,
  });
}

/**
 * Hook to update an existing quote
 */
type UseUpdateQuoteOptions = Omit<
  UseMutationOptions<
    { quote: quotesApi.Quote; pricing?: quotesApi.PricingCalculation },
    Error,
    { id: string; data: quotesApi.UpdateQuoteRequest }
  >,
  'mutationFn'
>;

export function useUpdateQuote(options?: UseUpdateQuoteOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: quotesApi.UpdateQuoteRequest }) =>
      quotesApi.updateQuote(id, data),
    onSuccess: (_data, variables, _context) => {
      // Avoid caching partial mutation payload shape ({ quote, pricing? }) into detail cache.
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

/**
 * Hook to update a quote's address without recalculating pricing
 */
export function useUpdateQuoteAddress(options?: Omit<import('@tanstack/react-query').UseMutationOptions<{ quote: quotesApi.Quote }, Error, { id: string; data: { origin?: { addressLine1?: string | null; locationName?: string | null }; destination?: { addressLine1?: string | null; locationName?: string | null } } }>, 'mutationFn'>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof quotesApi.updateQuoteAddress>[1] }) => quotesApi.updateQuoteAddress(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

/**
 * Hook to delete a quote
 */
type UseDeleteQuoteOptions = Omit<
  UseMutationOptions<void, Error, string>,
  'mutationFn'
>;

export function useDeleteQuote(options?: UseDeleteQuoteOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => quotesApi.deleteQuote(id),
    onSuccess: (_, variables) => {
      // Remove the deleted quote from cache
      queryClient.removeQueries({ queryKey: quoteKeys.detail(variables) });
      // Invalidate and refetch quotes list
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

/**
 * Hook to calculate quote pricing
 */
type UseCalculatePricingOptions = Omit<
  UseMutationOptions<
    quotesApi.PricingCalculationResponse,
    Error,
    quotesApi.CalculatePricingRequest
  >,
  'mutationFn'
>;

export function useCalculatePricing(options?: UseCalculatePricingOptions) {
  return useMutation({
    mutationFn: (data: quotesApi.CalculatePricingRequest) => quotesApi.calculatePricing(data),
    ...options,
  });
}

/**
 * Hook to fetch pricing calculation (uses useQuery for automatic deduplication)
 */
export function usePricingQuery(params: quotesApi.CalculatePricingRequest | null) {
  return useQuery({
    queryKey: quoteKeys.pricing(params!),
    queryFn: () => quotesApi.calculatePricing(params!),
    enabled: !!params && !!params.organizationId && !!params.cargoItems?.length,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 60000, // Keep in cache for 1 minute
  });
}

/**
 * Hook to submit quote for review
 */
type UseSubmitForReviewOptions = Omit<
  UseMutationOptions<quotesApi.Quote, Error, string>,
  'mutationFn'
>;

export function useSubmitForReview(options?: UseSubmitForReviewOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => quotesApi.submitForReview(id),
    onSuccess: (_, variables) => {
      // Invalidate detail to refetch full QuoteWithDetails (API returns partial Quote)
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

/**
 * Hook to direct book a quote
 */
type OrderReferenceMutationVariables = {
  id: string;
  orderReferenceNumber: string;
  poFile?: File | null;
};

type UseDirectBookOptions = Omit<
  UseMutationOptions<quotesApi.Quote, Error, OrderReferenceMutationVariables>,
  'mutationFn'
>;

export function useDirectBook(options?: UseDirectBookOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, orderReferenceNumber, poFile }: OrderReferenceMutationVariables) =>
      quotesApi.directBook(id, orderReferenceNumber, poFile),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

/**
 * Hook to approve a reviewed quote (client)
 */
type UseApproveQuoteOptions = Omit<
  UseMutationOptions<quotesApi.Quote, Error, OrderReferenceMutationVariables>,
  'mutationFn'
>;

export function useApproveQuote(options?: UseApproveQuoteOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, orderReferenceNumber, poFile }: OrderReferenceMutationVariables) =>
      quotesApi.approveQuote(id, orderReferenceNumber, poFile),
    onSuccess: (_data, variables, _context) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

/**
 * Hook to reject a quote (client)
 */
type UseRejectQuoteOptions = Omit<
  UseMutationOptions<quotesApi.Quote, Error, { id: string; reason?: string }>,
  'mutationFn'
>;

export function useRejectQuote(options?: UseRejectQuoteOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      quotesApi.rejectQuote(id, reason),
    onSuccess: (_data, variables, _context) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

// ==================== Operations Hooks ====================

/**
 * Hook to fetch all quotes (operations)
 */
export function useAllQuotes(params?: quotesApi.QuotesListParams) {
  return useQuery({
    queryKey: quoteKeys.list(params),
    queryFn: () => quotesApi.listAllQuotes(params),
  });
}

/**
 * Hook to update quote pricing (operations)
 */
type UseUpdateQuotePricingOptions = Omit<
  UseMutationOptions<
    { quote: quotesApi.Quote },
    Error,
    { id: string; data: quotesApi.UpdateQuotePricingRequest }
  >,
  'mutationFn'
>;

export function useUpdateQuotePricing(options?: UseUpdateQuotePricingOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: quotesApi.UpdateQuotePricingRequest }) =>
      quotesApi.updateQuotePricing(id, data),
    onSuccess: (_data, variables, _context) => {
      // Avoid caching partial mutation payload shape ({ quote }) into detail cache.
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

/**
 * Hook to submit reviewed quote (operations)
 */
export function useSubmitReviewed(options?: Omit<UseMutationOptions<quotesApi.Quote, Error, SubmitReviewedVariables>, 'mutationFn'>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: SubmitReviewedVariables) => quotesApi.submitReviewed(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

interface SubmitReviewedVariables {
  id: string;
  data?: quotesApi.SubmitReviewedRequest;
}

/**
 * Hook to approve direct booking (operations)
 */
export function useApproveDirectBook(
  options?: Omit<UseMutationOptions<quotesApi.Quote, Error, OrderReferenceMutationVariables>, 'mutationFn'>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, orderReferenceNumber, poFile }: OrderReferenceMutationVariables) =>
      quotesApi.approveDirectBook(id, orderReferenceNumber, poFile),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

/**
 * Hook to Confirm Booking for a READY_TO_BOOK quote (STD-590): records the TMS
 * Job Number and the fixed pickup/delivery date & time and completes the quote
 * in a single call.
 */
export function useCompleteQuote(
  options?: Omit<
    UseMutationOptions<quotesApi.Quote, Error, { id: string } & quotesApi.CompleteBookingInput>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & quotesApi.CompleteBookingInput) =>
      quotesApi.completeQuote(id, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

/**
 * Hook to edit a booked (COMPLETED) quote's Job Reference Number and/or fixed
 * pickup/delivery schedule (STD-591).
 */
export function useUpdateBooking(
  options?: Omit<
    UseMutationOptions<quotesApi.Quote, Error, { id: string } & quotesApi.UpdateBookingInput>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & quotesApi.UpdateBookingInput) =>
      quotesApi.updateBooking(id, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

/**
 * Hook to cancel a quote (operations)
 */
export function useCancelQuote(options?: Omit<UseMutationOptions<quotesApi.Quote, Error, { id: string; reason?: string }>, 'mutationFn'>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      quotesApi.cancelQuote(id, reason),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

/**
 * Hook to revive a cancelled quote (operations)
 */
export function useReviveQuote(options?: Omit<UseMutationOptions<quotesApi.Quote, Error, string>, 'mutationFn'>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => quotesApi.reviveQuote(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

// ==================== Custom Additional Services (STD-460) ====================

/**
 * Hook to add a custom service component to a quote
 */
export function useAddCustomService(
  options?: Omit<UseMutationOptions<{ quote: quotesApi.Quote }, Error, { id: string; name: string; baseCost: number; markupPercentage: number }>, 'mutationFn'>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name, baseCost, markupPercentage }: { id: string; name: string; baseCost: number; markupPercentage: number }) =>
      quotesApi.addCustomService(id, { name, baseCost, markupPercentage }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

/**
 * Hook to update a custom service component
 */
export function useUpdateCustomService(
  options?: Omit<
    UseMutationOptions<
      { quote: quotesApi.Quote },
      Error,
      { id: string; componentId: string; name?: string; baseCost?: number; markupPercentage?: number }
    >,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, componentId, name, baseCost, markupPercentage }: { id: string; componentId: string; name?: string; baseCost?: number; markupPercentage?: number }) =>
      quotesApi.updateCustomService(id, componentId, { name, baseCost, markupPercentage }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

/**
 * Hook to delete a custom service component
 */
export function useDeleteCustomService(
  options?: Omit<UseMutationOptions<{ quote: quotesApi.Quote }, Error, { id: string; componentId: string }>, 'mutationFn'>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, componentId }: { id: string; componentId: string }) =>
      quotesApi.deleteCustomService(id, componentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    ...options,
  });
}

// ==================== Activity Feed Hooks ====================

/**
 * Hook to fetch organization activity feed
 */
export function useOrganizationActivities(organizationId: string, limit?: number) {
  return useQuery({
    queryKey: quoteKeys.activities(organizationId, limit),
    queryFn: () => quotesApi.getOrganizationActivities(organizationId, limit),
    enabled: !!organizationId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch quote summary for dashboard KPIs
 */
export function useQuoteSummary(organizationId: string) {
  return useQuery({
    queryKey: quoteKeys.summary(organizationId),
    queryFn: () => quotesApi.getQuoteSummary(organizationId),
    enabled: !!organizationId,
    staleTime: 60000, // 1 minute - stats don't change frequently
  });
}

// ==================== Comment Hooks ====================

/**
 * Hook to create a comment on a quote
 */
export function useCreateQuoteComment(quoteId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: string | { message: string; file?: File | null }) => {
      if (typeof variables === 'string') {
        return quotesApi.createQuoteComment(quoteId, variables);
      }
      return quotesApi.createQuoteComment(quoteId, variables.message, variables.file);
    },
    onSuccess: () => {
      // Invalidate the quote to refetch activities including new comment
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(quoteId) });
    },
  });
}

/**
 * Hook to create an internal comment on a quote
 */
export function useCreateQuoteInternalComment(quoteId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (message: string) => quotesApi.createQuoteInternalComment(quoteId, message),
    onSuccess: () => {
      // Invalidate the quote to refetch activities including new internal comment
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(quoteId) });
    },
  });
}
